import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { identity, noop } from "rxjs";
import colors from "wwwroot/json/colors.json";
import { IConfidenceMapping, IConfidenceSettings } from "@gtmhub/core";
import { ConfidenceScale, ConfidenceType } from "@webapp/configuration/models/configuration.model";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { ConfidenceSettingsService } from "@webapp/okrs/metrics/services/confidence-settings.service";
import { findRange, getConfidenceFloatNumber, getFloatedMappings } from "@webapp/okrs/utils/confidence-level.utils";

@Component({
  selector: "confidence-level-selector",
  templateUrl: "./confidence-level-selector.component.html",
  styleUrls: ["./confidence-level-selector.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConfidenceLevelSelectorComponent),
      multi: true,
    },
  ],
})
export class ConfidenceLevelSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() public value: number;
  @Input() public confidence: unknown;

  @Output() public readonly confidenceChange = new EventEmitter<unknown>();

  public onChange = identity;
  public onTouch = noop;
  public scaleFactor = 10;
  public confidenceType: ConfidenceType;
  public confidenceValue: number;
  public confidenceScale: ConfidenceScale;
  public mappings: IConfidenceMapping[];
  public currentMapping: IConfidenceMapping;
  public textTypeColorClassMapping: Record<string, string>;
  public ariaLabelsMapForTextLevels: Record<string, string> = {};
  private confidenceSettings: IConfidenceSettings;

  constructor(private confidenceSettingsService: ConfidenceSettingsService, private cdRef: ChangeDetectorRef) {
    const colorsFromSettings = colors.colorScaleAndConfidenceLevelColors;

    this.textTypeColorClassMapping = {};

    // Count from 1 to match the design handoff
    // And reorder the colors manually - new design handoff counts vertically first whereas the color.json has then horizontally first
    this.textTypeColorClassMapping = {
      [colorsFromSettings[0]]: "type-1",
      [colorsFromSettings[9]]: "type-2",
      [colorsFromSettings[1]]: "type-3",
      [colorsFromSettings[10]]: "type-4",
      [colorsFromSettings[2]]: "type-5",
      [colorsFromSettings[11]]: "type-6",
      [colorsFromSettings[3]]: "type-7",
      [colorsFromSettings[12]]: "type-8",
      [colorsFromSettings[4]]: "type-9",
      [colorsFromSettings[13]]: "type-10",
      [colorsFromSettings[5]]: "type-11",
      [colorsFromSettings[14]]: "type-12",
      [colorsFromSettings[6]]: "type-13",
      [colorsFromSettings[15]]: "type-14",
      [colorsFromSettings[7]]: "type-15",
      [colorsFromSettings[16]]: "type-16",
      [colorsFromSettings[8]]: "type-17",
      [colorsFromSettings[18]]: "type-17",
      [colorsFromSettings[17]]: "type-18",

      // The Default Low and High confidence levels use colors NOT in the colors.json
      "#ffc400": "type-1",
      "#18C191": "type-13",
    };
  }

  public ngOnInit(): void {
    this.confidenceSettings = this.confidenceSettingsService.getConfidenceSettings();
    this.confidenceScale = this.confidenceSettingsService.getConfidenceScale();
    this.confidenceType = this.confidenceSettings?.confidenceType ?? "numeric";

    const confidenceMappingCopy = this.confidenceSettings.confidenceMapping.map((it) => Object.assign({}, it));
    this.mappings = getFloatedMappings(
      confidenceMappingCopy.sort((a: IConfidenceMapping, b: IConfidenceMapping) => a.range.to - b.range.to),
      this.scaleFactor
    );

    this.setConfidenceValueAndCurrentMapping();
    this.generateAriaLabelsMapForTextLevels();
  }

  public writeValue(value: number): void {
    this.confidence = value;
    this.value = value;
    this.generateAriaLabelsMapForTextLevels();
    this.setConfidenceValueAndCurrentMapping();
  }

  public registerOnChange(fn: typeof identity): void {
    this.onChange = fn;
  }

  public registerOnTouched(): void {
    // required by ControlValueAccessor
  }

  public setCurrentMapping(mapping: IConfidenceMapping): void {
    this.currentMapping = mapping;
    this.confidence = mapping.range.to;
    this.confidenceChange.emit(this.confidence);
    this.emitConfidenceChange();
  }

  public setConfidence(confidence: number): void {
    this.confidence = Math.round(confidence * 100 + Number.EPSILON) / 100;
    this.emitConfidenceChange();
  }

  public sliderTipFormatter = (value: number): string => {
    if (this.confidenceScale === "major") {
      return `${value * 10}/10`;
    } else {
      return `${value}`;
    }
  };

  private emitConfidenceChange(): void {
    this.onChange(this.confidence);
    this.confidenceChange.emit(this.confidence);
    this.cdRef.detectChanges();
  }

  private setConfidenceValueAndCurrentMapping(): void {
    this.confidenceValue = getConfidenceFloatNumber(this.value, this.scaleFactor);
    this.currentMapping = findRange(this.confidenceValue, this.mappings);
    this.cdRef.detectChanges();
  }

  private generateAriaLabelsMapForTextLevels(): void {
    const ariaLabelsMapForTextLevels = {};

    for (const mapping of this.mappings) {
      ariaLabelsMapForTextLevels[mapping.name] = `${localize("confidence_level_cap")} ${mapping.name}`;
    }

    this.ariaLabelsMapForTextLevels = ariaLabelsMapForTextLevels;
  }
}
